import { Checkbox } from '@carbon/react';
import { ChoiceDropdown, DateRangeFilter, PageHeader, StatusTag, useFilters, useFormat, useReferenceOptions } from '@pos/admin-ui';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useNavigate } from 'react-router';
import { dayRange } from '../logs/dates';
import { MultiChoice } from '../logs/fields';
import { LogTable, type LogQuery } from '../logs/LogTable';
import { EINVOICE_STATUSES, einvoicesKey, listEinvoices, type EinvoiceSummary } from './einvoiceApi';

/**
 * E-invoices › Invoices (ADR 0048, FR-EIN-05): the e-invoice HO issues for every paid order, newest first. HO sends them to
 * the provider in each store's order; one that fails waits here to be corrected and sent again, and one refunded after it
 * was issued is flagged to be adjusted at the provider.
 */
export function EinvoicesPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const navigate = useNavigate();
  const stores = useReferenceOptions('org.store');
  const filters = useFilters({ status: '', store: '', adjust: '', from: '', to: '' });

  const storeName = (id: string) => stores.data?.find((s) => s.value === id)?.label ?? '';
  const statusName = (status: string) => t(`einvoice.status.${status}` as I18nKey, { defaultValue: status });

  const load = (q: LogQuery) => {
    const { status, store, adjust, from, to } = filters.applied;
    return listEinvoices({
      status,
      storeId: store,
      needsAdjustment: adjust === 'yes' ? true : undefined,
      ...dayRange(from ?? '', to ?? ''),
      ...q,
    });
  };

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('einvoice.list.title')} description={t('einvoice.list.intro')} />
      <LogTable<EinvoiceSummary>
        label={t('einvoice.list.title')}
        queryKey={einvoicesKey}
        load={load}
        rowId={(r) => r.id}
        filters={filters}
        filterFields={
          <>
            <MultiChoice
              id="einvoice-status"
              label={t('einvoice.columns.status')}
              choices={EINVOICE_STATUSES.map((s) => ({ id: s, label: statusName(s) }))}
              value={filters.draft.status ?? ''}
              onChange={(v) => filters.set('status', v)}
            />
            <ChoiceDropdown
              id="einvoice-store"
              label={t('einvoice.columns.store')}
              choices={(stores.data ?? []).map((s) => ({ id: s.value, label: s.label }))}
              value={filters.draft.store ?? ''}
              onChange={(v) => filters.set('store', v)}
            />
            <Checkbox
              id="einvoice-adjust"
              labelText={t('einvoice.list.needsAdjustmentOnly')}
              checked={filters.draft.adjust === 'yes'}
              onChange={(_, { checked }) => filters.set('adjust', checked ? 'yes' : '')}
            />
            <DateRangeFilter
              id="einvoice-date"
              legendText={t('einvoice.columns.paidAt')}
              from={filters.draft.from ?? ''}
              to={filters.draft.to ?? ''}
              onChange={(r) => filters.setDraft((d) => ({ ...d, ...r }))}
            />
          </>
        }
        onOpen={(r) => void navigate(`/einvoice/invoices/${r.id}`)}
        columns={[
          { key: 'paidAt', header: t('einvoice.columns.paidAt'), render: (r) => format.dateTime(r.paidAt) },
          { key: 'order', header: t('einvoice.columns.order'), render: (r) => r.orderNumber },
          { key: 'store', header: t('einvoice.columns.store'), render: (r) => storeName(r.storeId) },
          {
            key: 'buyer',
            header: t('einvoice.columns.buyer'),
            render: (r) => (r.buyerType === 'company' ? (r.buyerCompanyName ?? '') : t('einvoice.buyer.retail')),
          },
          { key: 'total', header: t('einvoice.columns.total'), render: (r) => format.money(r.total, r.currencyCode) },
          { key: 'invoiceNo', header: t('einvoice.columns.invoiceNo'), render: (r) => r.invoiceNo ?? '' },
          {
            key: 'status',
            header: t('einvoice.columns.status'),
            render: (r) => (
              <>
                <StatusTag status={r.status}>{statusName(r.status)}</StatusTag>
                {r.needsAdjustment && <StatusTag status="refund">{t('einvoice.needsAdjustment')}</StatusTag>}
              </>
            ),
          },
        ]}
      />
    </section>
  );
}
